import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";

import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";

import { datadogRum } from "@datadog/browser-rum";

import { useAppSelector } from "app/hooks";
import {
  selectCurrentPractice,
  selectCurrentUser
} from "features/auth/auth-slice";
import useUserRoles from "hooks/use-user-roles.hook";
import { useGetQuickBooksConnectionHealthQuery } from "services/quickbooks";
import type { QuickBooksConnectionHealth } from "services/types/quickbooks.type";

import { useQboBannerDailyWindow } from "./use-qbo-banner-daily-window.hook";

export const QBO_TOKENS_PATH = "/quickbooks/tokens";

/**
 * Tells a practice, in app, that QuickBooks has stopped syncing because its connection expired.
 *
 * Until this existed the only signal was an email to one arbitrary business owner (see
 * `core/clients/quickbooks.py`), so a practice whose owner missed it kept booking sales that
 * silently never reached QBO.
 *
 * Audience is exactly `canAccessQBO` — Business owners, Suno, or the per-user "Access to
 * QuickBooks" grant — AND a practice with QuickBooks enabled. That is the same pair that gates the
 * QuickBooks nav entry and the backend's `require_qbo_access`; pointing anyone else at a Tokens
 * page they cannot open would be worse than silence. Note this deliberately excludes
 * Administrators, who do not have QBO access today.
 */
export default function QboReconnectBanner() {
  const currentUser = useAppSelector(selectCurrentUser);
  const currentPractice = useAppSelector(selectCurrentPractice);
  const { canAccessQBO, isScheduler } = useUserRoles();

  // `isScheduler` mirrors `routes.tsx`, which registers `quickbooks/*` only for
  // `!isScheduler && canAccessQBO`. A scheduler who also holds the QBO grant would otherwise get a
  // Reconnect button pointing at a route that does not exist for them.
  const enabled = Boolean(
    canAccessQBO &&
      !isScheduler &&
      currentPractice?.is_quickbooks_enabled &&
      currentUser?.id
  );

  const { data } = useGetQuickBooksConnectionHealthQuery(undefined, {
    skip: !enabled
  });

  // RTK Query's `skip` does NOT clear `data` — it keeps returning the last response. A
  // multi-practice user switching into a practice without QuickBooks would otherwise keep seeing
  // the previous practice's banner, so the derived value is gated on the same condition as `skip`.
  const health = enabled ? data : undefined;

  if (!health?.auth_failed || !currentUser?.id) return null;

  return (
    <QboReconnectBannerBar userId={String(currentUser.id)} health={health} />
  );
}

/**
 * Split from the gate above so the daily-window hook only ever mounts with a real user id: its
 * stored state is read once in a `useState` initializer, and reading it against a placeholder key
 * before auth resolves would show a banner the user had already been shown today.
 */
function QboReconnectBannerBar({
  userId,
  health
}: {
  userId: string;
  health: QuickBooksConnectionHealth;
}) {
  const { visible, markShown, dismiss } = useQboBannerDailyWindow(userId);

  // Guards the pair below against StrictMode's double-invoked effect, which would otherwise
  // double-count the metric. A ref, not module state: on a genuine remount the timestamp
  // `markShown` just wrote is already in storage, so `visible` is false and this returns above.
  const recordedRef = useRef(false);

  // Rendering is what spends the day's single appearance — `markShown` records it immediately, so
  // the banner stays up for this page load and then does not come back until tomorrow's 2pm
  // Eastern. Without this it rendered on every page load until someone clicked Dismiss, which is
  // what made it intrusive.
  useEffect(() => {
    if (!visible || recordedRef.current) return;
    recordedRef.current = true;
    markShown();
    datadogRum.addAction("qbo_reconnect_banner_shown", {
      connection_count: health.connections.length
    });
  }, [visible, markShown, health.connections.length]);

  if (!visible) return null;

  // Name the specific companies only when a practice has more than one, so the common
  // single-company case reads as a sentence rather than as a report. Gated on the connection
  // count, not the name count: two broken connections where only one is named must still say that
  // two are broken, so it falls back to the count when any name is missing.
  const names = health.connections.map(({ name }) => name).filter(Boolean);
  const companySuffix =
    health.connections.length > 1
      ? names.length === health.connections.length
        ? ` (${names.join(", ")})`
        : ` (${health.connections.length} companies)`
      : "";

  return (
    <Box
      role="alert"
      data-testid="qbo-reconnect-banner"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 2,
        py: 0.75,
        bgcolor: (theme) => alpha(theme.palette.warning.main, 0.12),
        borderBottom: (theme) =>
          `1px solid ${alpha(theme.palette.warning.main, 0.3)}`
      }}
    >
      <WarningAmberOutlinedIcon
        aria-hidden="true"
        fontSize="small"
        sx={{ color: "warning.main", flexShrink: 0 }}
      />

      <Typography variant="body2" sx={{ flex: 1 }}>
        <strong>QuickBooks is disconnected{companySuffix}.</strong> Nothing has
        synced since the connection expired. To restore it, open the Tokens
        page, click <strong>Authorize Quickbook</strong> and sign in to
        QuickBooks — syncing then resumes automatically.
      </Typography>

      <Button
        component={Link}
        to={QBO_TOKENS_PATH}
        size="small"
        variant="outlined"
        color="warning"
        data-testid="qbo-reconnect-banner-action"
        sx={{ flexShrink: 0 }}
      >
        Reconnect
      </Button>

      <Button
        size="small"
        variant="text"
        onClick={dismiss}
        aria-label="Dismiss QuickBooks disconnection notice for today"
        data-testid="qbo-reconnect-banner-dismiss"
        sx={{ flexShrink: 0, color: "text.primary" }}
      >
        Dismiss
      </Button>
    </Box>
  );
}
